import { motion } from 'framer-motion';
import { Cpu, ShieldCheck, Zap, Radio, Lock, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';

const features = [
    {
        icon: Cpu,
        title: "Neural Matching",
        description: "Our AI algorithm analyzes millions of route combinations to match cargoes with the perfect carrier in milliseconds.",
        color: "text-blue-600 dark:text-blue-400",
        bg: "bg-blue-600/10 dark:bg-blue-400/10",
        border: "group-hover:border-blue-600/50 dark:group-hover:border-blue-400/50"
    },
    {
        icon: ShieldCheck,
        title: "Quantum Secured",
        description: "Every transaction is cryptographically verified. Payments are held in smart escrow until delivery is confirmed.",
        color: "text-emerald-600 dark:text-emerald-400",
        bg: "bg-emerald-600/10 dark:bg-emerald-400/10",
        border: "group-hover:border-emerald-600/50 dark:group-hover:border-emerald-400/50"
    },
    {
        icon: Radio,
        title: "Hyper-Tracking",
        description: "Real-time telemetry gives you sub-second location updates and predictive ETA adjustments.",
        color: "text-purple-600 dark:text-purple-400",
        bg: "bg-purple-600/10 dark:bg-purple-400/10",
        border: "group-hover:border-purple-600/50 dark:group-hover:border-purple-400/50"
    },
    {
        icon: Lock,
        title: "Smart Contracts",
        description: "Automated agreements that execute instantly upon delivery proof, eliminating paperwork and delays.",
        color: "text-amber-600 dark:text-amber-400",
        bg: "bg-amber-600/10 dark:bg-amber-400/10",
        border: "group-hover:border-amber-600/50 dark:group-hover:border-amber-400/50"
    },
    {
        icon: Zap,
        title: "Instant Settlement",
        description: "Transporters get paid within minutes of job completion. No more 30-day payment cycles.",
        color: "text-pink-600 dark:text-pink-400",
        bg: "bg-pink-600/10 dark:bg-pink-400/10",
        border: "group-hover:border-pink-600/50 dark:group-hover:border-pink-400/50"
    },
    {
        icon: Activity,
        title: "Predictive Load",
        description: "Demand forecasting helps transporters position their fleet before the jobs even appear.",
        color: "text-cyan-600 dark:text-cyan-400",
        bg: "bg-cyan-600/10 dark:bg-cyan-400/10",
        border: "group-hover:border-cyan-600/50 dark:group-hover:border-cyan-400/50"
    }
];

const TechFeatures = () => {
    return (
        <section className="py-32 bg-background relative overflow-hidden transition-colors duration-500">
            {/* Ambient Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/20 blur-[120px] rounded-full opacity-20 pointer-events-none" />

            <div className="container px-4 relative z-10">
                <div className="text-center mb-24">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-foreground via-foreground/80 to-foreground/50 tracking-tighter mb-6"
                    >
                        Intelligence Core
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-xl text-muted-foreground max-w-2xl mx-auto"
                    >
                        Built on a stack of advanced technologies designed to make logistics
                        invisible, instantaneous, and infallible.
                    </motion.p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                            viewport={{ once: true }}
                            className={cn(
                                "group relative p-8 rounded-3xl bg-foreground/5 border border-foreground/10 backdrop-blur-xl overflow-hidden hover:bg-foreground/10 transition-all duration-500",
                                feature.border
                            )}
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-foreground/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                            <div className="relative z-10">
                                <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110", feature.bg)}>
                                    <feature.icon className={cn("w-7 h-7", feature.color)} />
                                </div>

                                <h3 className="text-2xl font-bold text-foreground mb-3 tracking-tight group-hover:text-primary transition-colors">
                                    {feature.title}
                                </h3>

                                <p className="text-muted-foreground leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TechFeatures;
